import "core-js/stable";
import "core-js/stable/string/match-all";

import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
import { createRoot } from "react-dom/client";
import React from "react";
import App from "./App";
import "./i18n";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { onBugsnagBreadcrumb } from "./helpers/bugsnag";
import { ErrorFallback } from "./errorFallback";
import { BSRTLThemeProvider } from "./rtl";

Bugsnag.start({
  apiKey: "972bfc1b50c1b6315e35ebf04d26a88b",
  collectUserIp: false,
  enabledReleaseStages: ["production"],
  releaseStage: process.env.NODE_ENV,
  plugins: [new BugsnagPluginReact()],
  onBreadcrumb: onBugsnagBreadcrumb,
});

if (typeof window !== "undefined") {
  window.ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;
}

// we know this plugin is loaded
const ErrorBoundary = Bugsnag.getPlugin("react")!.createErrorBoundary(React);

// Render the app
const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

// Reuse a single React root across module re-evaluations (e.g. when Vite's
// dev server / Fast Refresh evaluates this entry module more than once on
// startup). Calling `createRoot` twice on the same DOM node triggers a
// React 19 warning and can cause downstream `removeChild` errors during
// cleanup, so we cache the root on the host element.
interface ReactRootHost extends HTMLElement {
  _hgReactRoot?: ReturnType<typeof createRoot>;
}
const host = rootElement as ReactRootHost;
const root = host._hgReactRoot ?? (host._hgReactRoot = createRoot(rootElement));
root.render(
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <React.StrictMode>
      <BSRTLThemeProvider>
        <App />
      </BSRTLThemeProvider>
    </React.StrictMode>
  </ErrorBoundary>,
);
